import * as DialogPrimitive from '@radix-ui/react-dialog';
import { type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { BillingEnforcementPolicy } from '../../services/billingEnforcementService';
import { billingProfileManagementPath } from '../../constants/billingProfilePaths';
import {
  BILLING_SCROLL_TO_MANAGE_LOCATION_STATE,
  handleBillingManagementLinkClick,
} from '../../utils/billingManagementLink';

const LINK =
  'inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus-visible:outline focus-visible:ring-2 focus-visible:ring-orange-500';

export function BillingUrgentNudgeDialog(props: {
  policy: BillingEnforcementPolicy;
  isOrgUser: boolean;
  open: boolean;
  onDismiss: () => void;
}): ReactElement | null {
  const location = useLocation();
  const billingPath = billingProfileManagementPath(props.isOrgUser);

  if (!props.open) {
    return null;
  }

  const s = props.policy.state;
  const isSuspended = s === 'suspended';
  const urgentLimitedBody = props.isOrgUser
    ? 'Your organization is in limited service due to a failed or overdue payment. Update your method now to avoid full suspension and restore AI and write access. Data is never deleted for non-payment.'
    : 'Your account is in limited service due to a failed or overdue payment. Update your method now to avoid full suspension and restore AI and write access. Data is never deleted for non-payment.';

  const mainDescription = isSuspended
    ? 'Service access is limited until payment succeeds. You can use billing, profile, and data export. All data is retained; nothing is deleted for non-payment.'
    : urgentLimitedBody;

  return (
    <DialogPrimitive.Root
      open={props.open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          props.onDismiss();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-[80] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          className='fixed left-[50%] top-[50%] z-[81] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-orange-200 bg-white p-6 shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2'
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className='m-0 text-lg font-semibold text-gray-900'>
            {isSuspended ? 'Account payment required' : 'Urgent: billing action needed'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className='mt-3 m-0 text-sm leading-relaxed text-gray-600'>
            {mainDescription}
          </DialogPrimitive.Description>
          {typeof props.policy.days_until_suspension === 'number' && !isSuspended ? (
            <p className='mt-2 m-0 text-sm font-medium text-orange-800'>
              {props.policy.days_until_suspension} day
              {props.policy.days_until_suspension === 1 ? '' : 's'} left before full suspension.
            </p>
          ) : null}
          <div className='mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end'>
            <Link
              to={billingPath}
              state={BILLING_SCROLL_TO_MANAGE_LOCATION_STATE}
              className={LINK}
              onClick={(e) => {
                handleBillingManagementLinkClick(e, location.pathname, billingPath);
                props.onDismiss();
              }}
            >
              {isSuspended ? 'Open billing' : 'Update payment now'}
            </Link>
            <DialogPrimitive.Close asChild>
              <button
                type='button'
                className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-gray-800 hover:bg-gray-50'
              >
                Continue
              </button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
